import { useState } from "react";
import { Formik, Form } from "formik";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import form from "./schemas/form";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import AuthApi from "api/auth";
import UserInfo from "./components/UserInfo";
import Address from "./components/Address";
import { useTranslation } from "react-i18next";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

function getSteps() {
  return ["Informations de client", "Adresse"];
}

function NewUser() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [activeStep, setActiveStep] = useState(0);
  const steps = getSteps();
  const { formId } = form;
  const [userInfo, setUserInfo] = useState({});
  // eslint-disable-next-line
  const [addressInfo, setAddressInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [formValid, setFormValid] = useState(false);
  const [touchedSteps, setTouchedSteps] = useState(false);
  const [serverErrors, setServerErrors] = useState({});

  const isLastStep = activeStep === steps.length - 1;

  const getInitialValues = () => ({
    username: userInfo.username || "",
    password: userInfo.password || "",
    password2: userInfo.password2 || "",
    firstname: userInfo.firstname || "",
    lastname: userInfo.lastname || "",
    phone: userInfo.phone || "",
    store_type: userInfo.store_type || "",
    address_details: addressInfo.address_details || "",
    city: addressInfo.city || "",
    latitude: addressInfo.latitude || "33.8869",
    longitude: addressInfo.longitude || "9.5375",
    old_user: userInfo.old_user || false,
  });

  const handleBack = () => setActiveStep(activeStep - 1);

  const handleSubmit = async () => {
    const payload = {
      address_detail: addressInfo.address_details || "",
      business_role_id: "client",
      city: addressInfo.city || "",
      firstname: userInfo.firstname || "",
      lastname: userInfo.lastname || "",
      latitude: addressInfo.latitude || "33.8869",
      longitude: addressInfo.longitude || "9.5375",
      password: userInfo.password || "",
      password2: userInfo.password2 || "",
      phone: userInfo.phone || "",
      store_type: userInfo.store_type?.value || "",
      username: `${userInfo.firstname || ""}_${userInfo.lastname || ""}`.replace(/\s+/g, "_"),
      old_user: userInfo.old_user || false,
    };

    if (isLastStep) {
      try {
        setLoading(true);
        await AuthApi.Register(payload);
        navigate("/pages/users/liste-des-clients");
        setActiveStep(0);
      } catch (error) {
        setLoading(false);
        console.error("Registration failed:", error);

        if (
          error.response &&
          error.response.status === 500 &&
          error.response.data
        ) {
          setServerErrors(error.response.data);
          setActiveStep(0);
          setTouchedSteps(true);
        }
      }
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleGoBack = () => navigate(-1);

  const handleUserInfoChange = (newInfo) => {
    setUserInfo((prevInfo) => ({ ...prevInfo, ...newInfo }));
  };

  const handleAddressInfoChange = (newInfo) => {
    setAddressInfo((prevInfo) => ({ ...prevInfo, ...newInfo }));
  };

  function getStepContent(stepIndex, formData) {
    switch (stepIndex) {
      case 0:
        return (
          <UserInfo
            formData={formData}
            onChange={handleUserInfoChange}
            validateFormCallback={setFormValid}
            touched={touchedSteps}
            serverErrors={serverErrors}
          />
        );
      case 1:
        return (
          <Address
            formData={formData}
            onChange={handleAddressInfoChange}
            validateFormCallback={setFormValid}
            touched={touchedSteps}
          />
        );
      default:
        return null;
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            {/* Go Back Button */}
            <SoftBox mb={2}>
              <SoftButton
                variant="outlined"
                color="secondary"
                onClick={handleGoBack}
                startIcon={<ArrowBackIcon />}
                sx={{
                  borderWidth: 2,
                  padding: "8px 16px",
                  textTransform: "none",
                  fontWeight: "bold",
                }}
              >
                {t("Retour")}
              </SoftButton>
            </SoftBox>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{t(label)}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Formik
              initialValues={getInitialValues()}
              enableReinitialize={true}
              onSubmit={() => {
                if (!formValid) {
                  setTouchedSteps(true);
                  setActiveStep(activeStep);
                } else {
                  setTouchedSteps(false);
                  if (isLastStep) {
                    handleSubmit();
                  } else {
                    setActiveStep(activeStep + 1);
                  }
                }
              }}
              validateOnChange={false}
            >
              {({ values, touched, isSubmitting, validateForm }) => (
                <Form id={formId} autoComplete="off">
                  <Card>
                    <SoftBox p={2}>
                      {getStepContent(activeStep, {
                        values,
                        touched,
                      })}
                      <SoftBox
                        mt={2}
                        width="100%"
                        display="flex"
                        justifyContent="space-between"
                      >
                        {activeStep === 0 ? (
                          <SoftBox />
                        ) : (
                          <SoftButton
                            variant="gradient"
                            color="light"
                            onClick={handleBack}
                          >
                            {t("Retour")}
                          </SoftButton>
                        )}
                        <SoftButton
                          disabled={loading}
                          type="submit"
                          variant="gradient"
                          color="dark"
                        >
                          {loading ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : isLastStep ? (
                            `${t("Enregistrer")}`
                          ) : (
                            `${t("Suivant")}`
                          )}
                        </SoftButton>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default NewUser;
